import React, { useState, useCallback, useRef } from "react";
import GlobeComponent from "./Globe";
import DashboardStats from "./DashboardStats";
import HFAPage from "./HFAPage";

// Pacific Ocean roughly centered at lng -165
const PACIFIC_CENTER = -165;
const PACIFIC_HALF_WIDTH = 60; // +/- 60 degrees → triggers ~10% sooner, covers lng -225 to -105
const TRANSITION_DURATION_MS = 1000;
const HYSTERESIS_FRAMES = 30; // ~0.5s at 60fps
// Where to jump when returning from HFA (east Asia coast, past the Pacific)
const SKIP_TO_LNG = 130;

function normalizeLng(lng) {
  while (lng > 180) lng -= 360;
  while (lng < -180) lng += 360;
  return lng;
}

function isInPacific(lng) {
  const normalized = normalizeLng(lng);
  return (
    normalized >= PACIFIC_CENTER - PACIFIC_HALF_WIDTH &&
    normalized <= PACIFIC_CENTER + PACIFIC_HALF_WIDTH
  );
}

export default function CombineHFAPage() {
  const [showHFA, setShowHFA] = useState(false);
  const lastStateRef = useRef(false);
  const hysteresisRef = useRef(0);
  const globeCompRef = useRef();

  const currentDay = new Date().getDate();
  const isOddDay = currentDay % 2 !== 0;

  const handleRotationUpdate = useCallback((lng) => {
    const pacific = isInPacific(lng);

    if (pacific !== lastStateRef.current) {
      hysteresisRef.current++;
      if (hysteresisRef.current > HYSTERESIS_FRAMES) {
        const wasShowingHFA = lastStateRef.current;
        lastStateRef.current = pacific;
        hysteresisRef.current = 0;
        setShowHFA(pacific);

        // When returning from HFA → globe, skip past the Pacific
        if (wasShowingHFA && !pacific) {
          if (globeCompRef.current) {
            globeCompRef.current.jumpToLng(SKIP_TO_LNG);
          }
        }
      }
    } else {
      hysteresisRef.current = 0;
    }
  }, []);

  return (
    <div className="app-border" style={{ position: "relative", width: "100vw", height: "100vh", overflow: "hidden" }}>
      {/* Layer 1: Globe dashboard (always rendered, always rotating) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          transition: `opacity ${TRANSITION_DURATION_MS}ms ease-in-out`,
          opacity: showHFA ? 0 : 1,
          zIndex: 1,
        }}
      >
        <div className="App">
          {isOddDay ? (
            <>
              <div className="dashboard-section">
                <DashboardStats position="left" />
              </div>
              <div className="globe-section">
                <div className="absolute top-[2rem] right-[3rem] z-10 font-['Geist_Mono'] text-sm fhd:text-xl font-bold text-[#A18287]">
                  INSPIRING THE WORLD TO WORK OUT
                </div>
                <GlobeComponent ref={globeCompRef} position="right" onRotationUpdate={handleRotationUpdate} />
              </div>
            </>
          ) : (
            <>
              <div className="globe-section">
                <div className="absolute top-[2rem] left-[3rem] z-10 font-['Geist_Mono'] text-sm fhd:text-xl font-bold text-[#A18287]">
                  INSPIRING THE WORLD TO WORK OUT
                </div>
                <GlobeComponent ref={globeCompRef} position="left" onRotationUpdate={handleRotationUpdate} />
              </div>
              <div className="dashboard-section">
                <DashboardStats position="right" />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Layer 2: HFA page overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          transition: `opacity ${TRANSITION_DURATION_MS}ms ease-in-out`,
          opacity: showHFA ? 1 : 0,
          pointerEvents: showHFA ? "auto" : "none",
          zIndex: 2,
        }}
      >
        <HFAPage />
      </div>
    </div>
  );
}
